import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AutomaticOnboarding } from './components/AutomaticOnboarding';
import { LeaveOnboardingButton } from './components/LeaveOnboardingButton';
import { OnboardingProgress } from './components/OnboardingProgress';
import { SKIP_CONFETTI_KEY_PREFIX } from './components/PricingStepContainer';
import { fireSideCannons } from './components/fireSideCannons';
import { setOnboardingChromeHidden } from './components/onboardingChromeStore';
import { GsMode, useGettingStartedOnboarding } from './components/useGettingStartedOnboarding';
import { IndexPath } from '@/Config/app/router/index.path';
import { QuickchatAiIcon } from '@/Domain/components/icons/QuickchatAiWordmark.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { deferSiteMapModal } from '@/UI/KnowledgeBase/SiteScrapeReview/siteMapFlags';
import { SCENARIO_ID } from '@/context';

/**
 * Which user gesture caused a `GettingStartedModeSelected` event.
 *
 * With automatic and manual unified under the URL-first screen, the
 * transitions are: Skip on the URL form (→ `manual`), the recovery
 * Switch-to-manual button on the error view (→ `manual`, but only after
 * a failed scrape), and the first-step Back link that bounces the user
 * off the manual checklist back to the URL form (→ `automatic`).
 * Clicking Start doesn't change the mode — automatic is the default —
 * so there's no `start_button` source.
 *
 * Kept as a string literal union (not an enum) so it renders as a plain
 * property value in PostHog without the enum key prefix.
 */
type GsModeChangeSource = 'skip_button' | 'error_switch_to_manual' | 'manual_back_to_url_step';

/**
 * GettingStarted page — scaffold for the new onboarding experience.
 *
 * First-run flow:
 *   1. URL-first screen       — user enters their website URL and either
 *                               clicks "Start" (→ automatic onboarding) or
 *                               "Skip" (→ manual checklist). The mode
 *                               selection is inlined into this screen so
 *                               there's no extra "pick your path" detour.
 *   2. Chosen flow renders    — automatic progress view or manual checklist
 *   3. Once the flow completes, the user is navigated to the AI Preview page
 *      (with a celebratory confetti animation that carries across the route
 *      change via `canvas-confetti`'s document-level canvas).
 *
 * Cloned from AiPreview.page.tsx as a starting point. Intentionally stripped of
 * all AI Preview analytics, connectors, and cross-feature widgets — this page
 * owns its own flow end-to-end via `useGettingStartedOnboarding`.
 */
export const GettingStartedPage = () => {
  const { scenario } = useParams<{ scenario: string }>();
  const navigate = useNavigate();
  const { capture } = usePostHogAnalytics();
  const {
    data,
    isLoading,
    steps,
    completed_count,
    total_count,
    skipStep,
    completeStep,
    isSkipPending,
    mode,
    setMode,
    isAutomaticComplete,
    setAutomaticComplete,
    externalAppsSelection,
    deploymentSelection,
  } = useGettingStartedOnboarding();

  // Post-Stripe hand-off: flip completion flags so the standard celebration fires.
  const hasHandledPostCheckoutRef = useRef(false);
  useEffect(() => {
    if (hasHandledPostCheckoutRef.current) return;
    if (isLoading || !data) return;
    if (sessionStorage.getItem('__qc_onboarding_post_checkout') !== '1') return;
    hasHandledPostCheckoutRef.current = true;
    sessionStorage.removeItem('__qc_onboarding_post_checkout');
    completeStep('gs_step_5', true);
    setAutomaticComplete(true);
  }, [isLoading, data, completeStep, setAutomaticComplete]);

  // URL draft for the automatic onboarding form. Lives at the page level so
  // it survives motion's `key={mode}` remount of the inner content — without
  // this, toggling automatic→manual→automatic resets the field, and PostHog
  // showed users (e.g. uid 70299) bouncing between modes 7 times in 90s,
  // each time losing what they had typed and re-entering it.
  const [urlDraft, setUrlDraft] = useState('');

  // Automatic flow fires `onComplete` once its backend job reaches the
  // `completed` state. `isAutomaticComplete` is persisted in the DB so a
  // refresh mid-navigation still ends up at the AI Preview page.
  const isManualComplete = mode === 'manual' && completed_count === total_count;
  const isOnboardingComplete = isManualComplete || (mode === 'automatic' && isAutomaticComplete);

  // Legacy ChatImplementations don't have a progress row. If one of their users
  // lands on `/getting-started` directly, bounce them to AI Preview rather than
  // rendering a broken URL-first screen (every mutation would 404).
  useEffect(() => {
    if (isLoading || data !== null || !scenario) return;
    navigate(`/i/${scenario}/${IndexPath.aiPreview}`, { replace: true });
  }, [isLoading, data, scenario, navigate]);

  // `setMode` also clears `automatic_flow_completed` when switching to a
  // non-automatic mode — bundled into a single PATCH inside the hook to
  // avoid the two-mutation race that was bouncing the user back to the
  // URL-first screen.
  //
  // `source` disambiguates which gesture caused the transition — in the
  // URL-first flow a single mode change can come from Start, Skip, or the
  // first-step Back, and the event by itself doesn't say which. Attaching
  // it here keeps the captured properties honest for funnel queries
  // (e.g. "how many users Skipped vs. returned from step 1 back to the
  // URL screen?"). Keep in sync with `GsModeChangeSource` below.
  const changeMode = useCallback(
    (next: GsMode, source: GsModeChangeSource) => {
      setMode(next);
      capture(PostHogEventEnum.GettingStartedModeSelected, { mode: next, source });
    },
    [setMode, capture],
  );

  // Top-of-funnel event: fire once per mount as soon as the progress row
  // has loaded. `is_resuming` is the "this user already did some work"
  // signal — any manual-step progress, any clicked-Skip-into-manual, or a
  // finished automatic run flips it true. Fresh `automatic` rows (the
  // default, no steps touched yet) are treated as first visits.
  // Ref-guarded to fire exactly once even across re-renders caused by
  // optimistic cache writes.
  const hasFiredViewedRef = useRef(false);
  const viewedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (hasFiredViewedRef.current || isLoading || !data) return;
    hasFiredViewedRef.current = true;
    viewedAtRef.current = Date.now();
    const isResuming = mode === 'manual' || completed_count > 0 || data.automatic_flow_completed;
    capture(PostHogEventEnum.GettingStartedViewed, {
      mode,
      is_resuming: isResuming,
      completed_step_count: completed_count,
    });
  }, [isLoading, data, mode, completed_count, capture]);

  // Abandonment-on-unmount: if the user fired Viewed but unmounts without
  // committing to a path, emit `GettingStartedModeSelectionAbandoned` so the
  // funnel can measure entry-screen bounce. "Committed" is read off the
  // (raw, un-normalized) progress row at unmount: any of `mode='manual'`
  // (Skip pressed), `automatic_flow_completed=true` (automatic flow done —
  // also fires Finished), or `knowledge_base_step_completed=true` (URL
  // submitted + WS completed). Refs feed the cleanup closure since `data`
  // and `capture` aren't dependencies of this empty-deps effect.
  //
  // Out of scope: explicit detection for `GettingStartedCallBooked` (no DB
  // side-effect; treated as abandonment, with the standalone CallBooked
  // event still capturing the engagement) and for users who fired
  // `GettingStartedAutomaticSubmitted` but bailed before the WS completed —
  // those legitimately *are* a bouncy state and benefit from being counted
  // here, with the analyst joining via `distinct_id` if needed.
  const dataRef = useRef(data);
  dataRef.current = data;
  const captureRef = useRef(capture);
  captureRef.current = capture;
  useEffect(
    () => () => {
      if (!hasFiredViewedRef.current) return;
      const d = dataRef.current;
      const advanced =
        d?.mode === 'manual' ||
        d?.automatic_flow_completed === true ||
        d?.knowledge_base_step_completed === true;
      if (advanced) return;
      captureRef.current(PostHogEventEnum.GettingStartedModeSelectionAbandoned, {
        dwell_seconds: viewedAtRef.current
          ? Math.round((Date.now() - viewedAtRef.current) / 1000)
          : 0,
        mode_at_unmount: d?.mode ?? 'choice',
        is_resuming: d?.mode === 'manual' || d?.automatic_flow_completed === true,
        exit_pathname: typeof document !== 'undefined' ? document.location.pathname : '',
      });
    },
    [],
  );

  // Keep the DefaultLayout chrome (sidebar + top bar) hidden for the entire
  // life of this page — including the 300ms grace window after completion
  // while the last step fades out and the confetti is firing. The chrome
  // only reappears when the component unmounts (on navigate), which lines
  // up exactly with AI Preview mounting — so the sidebar / topbar slot in
  // at the same moment the new page paints instead of popping in early.
  useEffect(() => {
    setOnboardingChromeHidden(true);
    return () => setOnboardingChromeHidden(false);
  }, []);

  // On the false→true transition of onboarding completion: kick off the Magic
  // UI "Side Cannons" confetti, then navigate to the AI Preview page after
  // a short grace window. During that window the page renders nothing
  // (renderContent returns null when `isOnboardingComplete`), so the chrome
  // reveal above can't push any onboarding content around — the user sees
  // the sidebar + topbar slot in over an empty sidebar-tinted backdrop, then
  // AI Preview mounts cleanly into the already-settled layout.
  //
  // `canvas-confetti` draws on a document-level canvas so particles keep
  // animating uninterrupted across the route change. The `wasCompleteRef`
  // guard prevents replaying the animation on reload if the user lands back
  // here with the completion flag already set — but in that case we still
  // need to redirect silently. Without the silent redirect the user gets
  // stranded on a blank `/getting-started` because `renderContent()` returns
  // null whenever `isOnboardingComplete` is true.
  // Live refs for the selection Sets so the completion effect can read them
  // on fire without listing them as deps. Including them as deps would re-run
  // the effect on unrelated re-renders (React Query writing a fresh cache
  // object on mutation success, etc.), whose cleanup would cancel the pending
  // navigate `setTimeout` below — leaving the user stuck on a blank screen.
  const externalAppsSelectionRef = useRef(externalAppsSelection);
  externalAppsSelectionRef.current = externalAppsSelection;
  const deploymentSelectionRef = useRef(deploymentSelection);
  deploymentSelectionRef.current = deploymentSelection;

  const wasCompleteRef = useRef(isOnboardingComplete);
  useEffect(() => {
    const wasComplete = wasCompleteRef.current;
    wasCompleteRef.current = isOnboardingComplete;
    if (!isOnboardingComplete || !scenario) return;

    if (wasComplete) {
      // The user landed on /getting-started with cached completion state
      // (e.g. browser back from AI Preview, or sidebar navigation after
      // having finished). `renderContent()` returns null in this state, so
      // without an explicit redirect they see a blank page with no way out.
      // Replace history so the back button doesn't bounce them right back.
      navigate(`/i/${scenario}/${IndexPath.aiPreview}`, { replace: true });
      return;
    }

    capture(PostHogEventEnum.GettingStartedFinished, {
      mode,
      external_apps_selection: Array.from(externalAppsSelectionRef.current),
      deployment_selection: Array.from(deploymentSelectionRef.current),
    });

    // Suppress when the discount modal already fired the burst on open.
    const skipConfettiKey = `${SKIP_CONFETTI_KEY_PREFIX}${SCENARIO_ID}`;
    const shouldSkipConfetti = sessionStorage.getItem(skipConfettiKey) === '1';
    if (shouldSkipConfetti) {
      sessionStorage.removeItem(skipConfettiKey);
    } else {
      fireSideCannons();
    }

    // Hold the sitewide-scrape review modal shut so the discount + confetti land
    // before it grows out of the dock (the dock keeps narrating meanwhile).
    deferSiteMapModal();

    const timer = setTimeout(() => {
      // Flip the chrome visible *in the same callback* as navigate so the
      // zustand store update and the route change batch into a single
      // commit. Doing it in the unmount cleanup instead (the default path)
      // lets AI Preview paint a frame before the sidebar / topbar slot in,
      // which is the "AI Preview shows up slightly before chrome" flicker.
      setOnboardingChromeHidden(false);
      navigate(`/i/${scenario}/${IndexPath.aiPreview}`);
    }, 300);
    return () => clearTimeout(timer);
  }, [isOnboardingComplete, scenario, navigate, capture, mode]);

  const renderContent = () => {
    // Completion triggers a navigate-away effect; render nothing in the brief
    // window between state flip and route change so the user doesn't see a
    // flash of empty onboarding chrome.
    if (isOnboardingComplete) return null;

    if (mode === 'manual') {
      return (
        // No max-height / inner scroll: when the prompt or step content grows
        // taller than the viewport, the outer `DefaultLayout` scroll container
        // takes over. The AI prompt textarea keeps its own max-height.
        //
        // No max-width on this wrapper: each step container inside
        // `OnboardingProgress` sets its own (max-w-lg for the regular checklist
        // steps 1–4, max-w-3xl for the wider PricingStepContainer), with the
        // AnimatePresence-driven cross-fade between them swapping containers
        // instead of resizing one. `items-center` keeps both centered.
        <div className="flex-col gap-4 flex z-10 w-full items-center">
          <OnboardingProgress
            steps={steps}
            completed_count={completed_count}
            total_count={total_count}
            skipStep={skipStep}
            completeStep={completeStep}
            isSkipPending={isSkipPending}
            onBackToUrlStep={() => changeMode('automatic', 'manual_back_to_url_step')}
          />
        </div>
      );
    }

    // `automatic` (the default for new rows and the mode the user commits to
    // by clicking Start). The URL-first screen is rendered here; Skip flips
    // `mode` to `'manual'` which re-routes through the branch above.
    // `AutomaticOnboarding` owns its own outer wrapper (testid, max-width,
    // scroll) so the URL step can run at `max-w-md` while every later step
    // gets the wider `max-w-lg` of the rest of the flow.
    return (
      <AutomaticOnboarding
        onComplete={() => setAutomaticComplete(true)}
        onSkipToManual={() => changeMode('manual', 'skip_button')}
        onSwitchToManual={() => changeMode('manual', 'error_switch_to_manual')}
        urlDraft={urlDraft}
        onUrlDraftChange={setUrlDraft}
      />
    );
  };

  return (
    // Full-bleed sidebar-tinted backdrop. `DefaultLayout`'s scroll container
    // wraps the page in a `p-6` gutter, so on its own a `bg-sidebar` on
    // `<main>` would leave a 24px ring of the default background along the
    // viewport edges. The standard `-m-6 p-6` full-bleed trick cancels the
    // outer gutter (negative margin) and re-applies the same spacing as
    // internal padding, letting the tint fill the scroll area edge-to-edge
    // while keeping the content padding identical to every other page.
    //
    // `min-h-[calc(100%+3rem)]` makes the wrapper cover the full scroll area
    // (the extra 3rem compensates for the parent's top+bottom padding that
    // the negative margin just pulled past).
    //
    // Bottom spacing: browsers collapse a flex scroll container's own
    // `padding-bottom` once its child overflows, so `pb-6` on the inner
    // wrapper disappears the moment scrolling kicks in. The reliable
    // workaround is a fixed-height `flex-shrink-0` spacer rendered *after*
    // the content inside the scroll area — a sibling spacer always takes its
    // declared height regardless of what the content above it does.
    //
    // `my-auto` on the content wrapper vertically centers short content but
    // collapses to 0 when content is taller than the viewport — the naive
    // `items-center` would instead push the top half of tall content above
    // the scroll area, making it unreachable.
    <div className="-m-6 p-6 bg-sidebar dark:bg-background min-h-[calc(100%+3rem)] flex flex-col relative">
      {!isOnboardingComplete && (
        <QuickchatAiIcon
          className="scale-[1.2] absolute top-6 left-6 text-foreground"
          data-testid="gs-onboarding-page-logo"
        />
      )}
      {!isOnboardingComplete && <LeaveOnboardingButton />}
      <main className="flex flex-col flex-1 max-w-7xl w-full mx-auto">
        <AnimatePresence mode="wait">
          <motion.div
            // Key on the active view so a mode switch triggers a fade+slide
            // exit/enter — matches the per-step transition used inside
            // `OnboardingChecklist` so the whole flow shares a motion
            // vocabulary.
            key={isOnboardingComplete ? 'complete' : mode}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            className="my-auto flex justify-center w-full"
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
        <div className="h-6 flex-shrink-0" aria-hidden />
      </main>
    </div>
  );
};
